import { Grid, Button, TextField, Alert, Divider } from '@mui/material';
import { Box } from '@mui/system';
import SendIcon from '@mui/icons-material/Send';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { RegexHelper } from 'src/shared/helpers/regexHelper';
import { previewGroupMessage } from 'src/shared/services/groupMessageService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { toast } from 'react-toastify';
import { GroupMessagePreviewPost } from '../models/groupMessagePreviewPost';

type GroupMessageEmailPreviewModalProps = {
  modalOpen: boolean;
  onSave: () => void;
  onClose: () => void;
};

export const GroupMessageEmailPreviewModal = (props: GroupMessageEmailPreviewModalProps) => {
  const { modalOpen, onSave, onClose } = props;
  const isMounted = useIsMounted();
  const { t } = useTranslation(['common', 'groupMessage']);

  const [loading, setLoading] = useState<boolean>(false);
  const [recipients, setRecipients] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { getValues } = useFormContext();

  const onHandleSave = useCallback(() => {
    onSave();
  }, [onSave]);

  const onHandleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSendPreview = useCallback(async () => {
    setLoading(true);
    const dataToPost = {
      title: getValues('subject'),
      message: getValues('body'),
      receivers: recipients,
      files: getValues('files'),
      sender: getValues('sender'),
      senderTitle: getValues('senderTitle'),
    } as GroupMessagePreviewPost;

    try {
      await previewGroupMessage(dataToPost);
      if (isMounted()) {
        onHandleSave();
        toast.success(<>{t('groupMessage:preview.sendSucess')}</>);
        ConsoleHelper.log('dataToPost previewGroupMessage', dataToPost);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      setLoading(false);
    }
  }, [getValues, isMounted, onHandleSave, recipients, t]);

  const handleChangeRecipient = (val: string) => {
    if (!val.match(RegexHelper.emailAddresses)) {
      setErrorMessage(t('common:validationMessages.multipleEmail'));
    } else {
      setErrorMessage('');
    }
    setRecipients(val);
  };

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      maxWidth='sm'
      dialogTitle={t('groupMessage:preview.modalHeader')}
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogContent={
        <FormLoaderWrapper loading={loading}>
          <>
            <Alert severity='info'>{t('groupMessage:preview.alertDescription')}</Alert>
            <Divider />
            <Box sx={{ p: 2 }}>
              <GridWrapper>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size='small'
                    label={t('groupMessage:preview.emailLabel')}
                    value={recipients}
                    placeholder={t('groupMessage:preview.emailPlaceholder')}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      handleChangeRecipient(event.target.value);
                    }}
                    error={!!errorMessage}
                    helperText={errorMessage || t('groupMessage:formFields.recipients_helperText')}
                  />
                </Grid>
              </GridWrapper>
            </Box>
          </>
        </FormLoaderWrapper>
      }
      dialogActions={
        <>
          <Button variant='outlined' disableElevation onClick={onHandleClose}>
            {t('common:button.cancel')}
          </Button>
          <Button
            variant='contained'
            disableElevation
            disabled={!!errorMessage || !recipients}
            onClick={handleSendPreview}
            endIcon={<SendIcon aria-hidden />}
          >
            {t('common:button.saveAndSend')}
          </Button>
        </>
      }
    />
  );
};
